'use client';

// ── New Profit Requirement — "second payout" example ─────────────────────────
// Figma: Moldura 12071:60033 (desktop, 792×653) and 12471:11134 (mobile, 366×1178).
// Two cards compared: A has enough new profit (Payout Cap limits), B has only
// $500 of new profit (New profit × 2 limits). Side by side on desktop, stacked
// on mobile. Every number is an illustrative example; sizes are Figma px / 8.

import type { ReactNode } from 'react';
import { ExampleLabel, FigureFrame } from '../primitives';

const T = 'mb-0 text-[2rem] leading-[3rem]';

function Card({ children, dataUi }: { children: ReactNode; dataUi: string }) {
  return (
    <div
      data-ui={dataUi}
      className="flex w-full flex-col gap-[1rem] rounded-[0.25rem] border border-black/10 px-[1rem] py-[2rem] lg:min-w-0 lg:flex-1"
    >
      {children}
    </div>
  );
}

function CardTitle({ children }: { children: ReactNode }) {
  return (
    <div className="flex w-full px-[1.5rem]">
      <p className={`${T} min-w-0 flex-1 font-medium text-black`}>{children}</p>
    </div>
  );
}

/** Underlined row (Figma "Linha" with border-bottom): 45px tall. */
function Line({ label, value, green = false }: { label: string; value: string; green?: boolean }) {
  return (
    <div className="flex w-full items-center justify-between gap-[2rem] border-b border-black/[0.06] px-[1.5rem] py-[1.25rem] lg:gap-0">
      <p className={`${T} min-w-0 flex-1 text-black/[0.64]`}>{label}</p>
      <p className={`${T} shrink-0 whitespace-nowrap font-medium ${green ? 'text-[#228e71]' : 'text-black'}`}>{value}</p>
    </div>
  );
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="flex w-full flex-col px-[1.5rem] pt-[1rem]">
      <p className={`${T} whitespace-nowrap font-medium text-black/40`}>{children}</p>
    </div>
  );
}

function LimitsChip() {
  return (
    <div className="flex h-[3rem] shrink-0 items-center rounded-[0.25rem] bg-[#001b14] px-[1rem] lg:h-[4rem]">
      <p className={`${T} whitespace-nowrap font-medium text-white`}>Limits</p>
    </div>
  );
}

function Value({ children }: { children: ReactNode }) {
  return <p className={`${T} shrink-0 whitespace-nowrap font-medium text-black`}>{children}</p>;
}

/** Gray row: text-only (24px) on mobile, fixed height on desktop. */
function GrayRow({
  label,
  children,
  desktopHeight = 'lg:h-[5rem]',
}: {
  label: string;
  children: ReactNode;
  desktopHeight?: string;
}) {
  return (
    <div
      className={`flex w-full items-center justify-between gap-[2rem] rounded-[0.25rem] bg-[#f2f4f3] px-[1.5rem] lg:gap-0 ${desktopHeight}`}
    >
      <p className={`${T} min-w-0 flex-1 text-black/[0.64]`}>{label}</p>
      {children}
    </div>
  );
}

function Maximum({ value, tone }: { value: string; tone: 'green' | 'red' }) {
  const green = tone === 'green';
  return (
    <div
      className={`flex w-full items-center justify-between rounded-[0.25rem] p-[1.5rem] ${
        green ? 'bg-[#e2f3ef]' : 'bg-[#ac0003]/[0.08]'
      }`}
    >
      <p className={`${T} whitespace-nowrap font-medium ${green ? 'text-[#196551]' : 'text-[#ac0003]'}`}>
        Your payout maximum
      </p>
      <p
        className={`mb-0 whitespace-nowrap text-[3rem] font-medium leading-[4rem] ${
          green ? 'text-[#228e71]' : 'text-[#ac0003]'
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function Note({ children, dataUi }: { children: ReactNode; dataUi: string }) {
  return (
    <div data-ui={dataUi} className="flex w-full px-[1.5rem]">
      <p className={`${T} min-w-0 flex-1 text-black/[0.64]`}>{children}</p>
    </div>
  );
}

export default function NewProfitRequirement() {
  return (
    // `main` sets text-wrap: balance globally; Figma wraps greedily.
    <FigureFrame data-ui="figure.newProfitRequirement" className="gap-[2.5rem] font-geist [text-wrap:wrap]">
      {/* Mobile breaks the eyebrow in two lines and drops the middle dot */}
      <ExampleLabel>
        EXAMPLE · SECOND PAYOUT
        <span className="hidden lg:inline"> · </span>
        <br className="lg:hidden" />
        TARGET $2,000
      </ExampleLabel>

      <div
        data-ui="figure.newProfitRequirement.comparison"
        className="flex w-full flex-col gap-[2rem] lg:flex-row lg:items-stretch"
      >
        {/* A · enough new profit */}
        <Card dataUi="figure.newProfitRequirement.card-a">
          <CardTitle>A · $1,000 new profit</CardTitle>
          <Line label="Profit left after payout 1" value="$3,500" />
          <Line label="New profit made since" value="$1,000" green />
          <Line label="Total profit now" value="$4,500" />
          <Eyebrow>PAYOUT LIMITS</Eyebrow>
          <GrayRow label="50% of total profit">
            <Value>$2,250</Value>
          </GrayRow>
          <GrayRow label="Payout Cap">
            <div className="flex shrink-0 items-center gap-[1rem]">
              <LimitsChip />
              <Value>$2,000</Value>
            </div>
          </GrayRow>
          <GrayRow label="New profit × 2 (half must be new)" desktopHeight="lg:h-[7rem]">
            <Value>$2,000</Value>
          </GrayRow>
          <Maximum value="$2,000" tone="green" />
          <Note dataUi="figure.newProfitRequirement.note-a">
            Enough new profit — the full $2,000 can be requested. The Payout Cap is the limit.
          </Note>
        </Card>

        {/* B · only $500 new profit */}
        <Card dataUi="figure.newProfitRequirement.card-b">
          <CardTitle>B · only $500 new profit</CardTitle>
          <Line label="Profit left after payout 1" value="$3,500" />
          <Line label="New profit made since" value="$500" green />
          <Line label="Total profit now" value="$4,000" />
          <Eyebrow>PAYOUT LIMITS</Eyebrow>
          <GrayRow label="50% of total profit">
            <Value>$2,000</Value>
          </GrayRow>
          <GrayRow label="Payout Cap">
            <Value>$2,000</Value>
          </GrayRow>
          {/* Mobile: label on its own line, chip + value right-aligned below (52px). Desktop: one 56px row. */}
          <div className="flex h-[6.5rem] w-full flex-col items-end gap-[0.5rem] rounded-[0.25rem] bg-[#f2f4f3] px-[1.5rem] lg:h-[7rem] lg:flex-row lg:items-center lg:justify-between lg:gap-0">
            <p className={`${T} w-full text-black/[0.64] lg:w-auto lg:min-w-0 lg:flex-1`}>
              New profit × 2 (half must be new)
            </p>
            <div className="flex shrink-0 items-center gap-[1rem]">
              <LimitsChip />
              <Value>$1,000</Value>
            </div>
          </div>
          <Maximum value="$1,000" tone="red" />
          <Note dataUi="figure.newProfitRequirement.note-b">
            Only <span className="font-medium text-black">$1,000</span> — because only $500 of new profit was made,
            and half of the payout has to be new profit.
          </Note>
        </Card>
      </div>
    </FigureFrame>
  );
}
